// New Project from a template (NewProjectView and `DocumentStore.apply` on the Mac, review B13): the project document
// made as `ProjectDocument.create` makes it (the template's type), then the template applied — its manuscript items with
// their Section types, Research & Notes items, the manuscript target and compile presets (`PlanEditor.applyTemplate`) —
// and put on the relay as a new project, as the sample is: a new project key, the summary sealed with it, each
// document's first snapshot. Returns the project's id and the scene to open.
import * as A from '@automerge/automerge';
import { isStageFormat, readProject, stageFormatForRegion, StoreEditor, type ProjectType, type ScriptFormat } from '@writing-suite/core';
import { PlanEditor, PROJECT_TEMPLATES, type ProjectTemplate } from '@writing-suite/core/plan';
import type { Api } from '@writing-suite/client/net/api.ts';
import { newCid, SyncConnection } from '@writing-suite/client/net/sync.ts';
import { putProject } from '@writing-suite/client/net/vault.ts';
import { random, seal } from '@writing-suite/client/shared/crypto.ts';

export interface NewProjectDoc {
  name: string;
  bytes: Uint8Array;
}

/** The documents of a new project from a template, and the node its first scene is. */
export function buildProject(o: { title: string; template: ProjectTemplate; scriptFormat?: ScriptFormat; locale?: string; author?: string; now?: Date }):
  { projectId: string; type: ProjectType; docs: NewProjectDoc[]; opening: string | null } {
  const now = o.now ?? new Date();
  // A play starts in its region's format (UK in Britain and Ireland, else US) unless one is chosen.
  const templateFormat = o.template.scriptFormat ?? 'screen';
  const scriptFormat = o.scriptFormat ?? (isStageFormat(templateFormat) ? stageFormatForRegion(o.locale?.split('-')[1]) : templateFormat);
  const project = StoreEditor.createProject({ title: o.title, type: o.template.type, scriptFormat, musical: o.template.musical, locale: o.locale, createdAt: now });
  const docs = new Map<string, A.Doc<unknown>>([['project', project]]);
  const editor = new PlanEditor(docs, { device: 'web', author: o.author ?? 'Me', now: () => now });
  const opening = editor.applyTemplate(o.template);
  const record = readProject(docs.get('project')!);
  return {
    projectId: record.id,
    type: o.template.type,
    docs: [...docs].map(([name, doc]) => ({ name, bytes: A.save(doc) })),
    opening,
  };
}

export const projectTemplates = () => PROJECT_TEMPLATES;

/** The browser's language as a project locale ("en-GB"), as new projects on the Mac take the device's. */
export function deviceLocale(): string {
  const lang = typeof navigator !== 'undefined' ? navigator.language : '';
  return /^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})*$/.test(lang) ? lang : 'en-GB';
}

/** Puts a new project on the relay: its key and sealed summary, then each document as its first snapshot. Returns the
 *  project's key (for anything else to seal with it, such as an import's attachments). */
export async function createProject(api: Api, account: { accountId: string; avk: Uint8Array }, built: ReturnType<typeof buildProject>, title: string): Promise<Uint8Array> {
  const key = random(32);
  await putProject(api, account.accountId, account.avk, built.projectId, key, {
    v: 1, title, type: built.type, words: 0, modifiedAt: new Date().toISOString(),
  }, { ifAbsent: true });
  const conn = new SyncConnection(api, built.projectId);
  await conn.connect();
  try {
    const { limits } = await conn.hello(0);
    for (const d of built.docs) {
      const heads = A.getHeads(A.load(d.bytes)).sort().join('');
      const r = await conn.append('snapshot', { doc: d.name, epoch: 0, lineage: '', cid: newCid(), heads, base: 0 }, await seal(key, d.bytes), limits);
      if (r.t === 'error') throw new Error(`${d.name}: ${r.message}`);
    }
  } finally {
    conn.close();
  }
  return key;
}
